import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { getLanguageFromPath } from '../../utils/routes';

const content = {
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: May 30, 2026',
    intro: 'Tarkov Stash respects your privacy. This policy explains what information may be collected when you use tarkovstash.com and how it is used.',
    sections: [
      {
        title: 'Information We Collect',
        body: 'We may collect non-personal usage data such as pages visited, device type, browser type, approximate location, referring pages, and interaction patterns. If you sign in or save progress, profile settings and game progress may be stored so the application can function.',
      },
      {
        title: 'Log Files',
        body: 'Like many websites, Tarkov Stash may use log files. These files can include IP address, browser type, internet service provider, date and time stamp, referring and exit pages, and click data. This information is used to analyze trends, administer the site, and improve performance.',
      },
      {
        title: 'Cookies and Google AdSense',
        body: 'Tarkov Stash may use cookies to remember preferences and improve the user experience. Third-party vendors, including Google, use cookies to serve ads based on a user\'s prior visits to this website or other websites. Google\'s use of advertising cookies enables it and its partners to serve ads to users based on visits to this site and other sites on the Internet.',
      },
      {
        title: 'Third-Party Services',
        body: 'We may use services such as Google AdSense, Firebase, analytics tools, and tarkov.dev API integrations. These services may process data according to their own privacy policies.',
      },
      {
        title: 'Your Choices',
        body: 'You can disable cookies in your browser settings. You can also opt out of personalized advertising by visiting Google Ads Settings.',
      },
      {
        title: 'Contact',
        body: 'For privacy questions, contact us at contact@tarkovstash.com.',
      },
    ],
  },
  tr: {
    title: 'Gizlilik Politikası',
    updated: 'Son güncelleme: 30 Mayıs 2026',
    intro: 'Tarkov Stash gizliliğinize önem verir. Bu politika, tarkovstash.com kullanımınız sırasında hangi bilgilerin toplanabileceğini ve nasıl kullanılacağını açıklar.',
    sections: [
      {
        title: 'Topladığımız Bilgiler',
        body: 'Ziyaret edilen sayfalar, cihaz türü, tarayıcı türü, yaklaşık konum, yönlendiren sayfalar ve etkileşim gibi kişisel olmayan kullanım verileri toplanabilir. Giriş yaparsanız veya ilerleme kaydederseniz, uygulamanın çalışması için profil ayarları ve oyun ilerlemesi saklanabilir.',
      },
      {
        title: 'Log Dosyaları',
        body: 'Birçok web sitesi gibi Tarkov Stash de log dosyaları kullanabilir. Bu dosyalar IP adresi, tarayıcı türü, internet servis sağlayıcısı, tarih ve saat bilgisi, yönlendiren ve çıkış sayfaları ile tıklama verilerini içerebilir. Bu bilgiler trendleri analiz etmek, siteyi yönetmek ve performansı iyileştirmek için kullanılır.',
      },
      {
        title: 'Çerezler ve Google AdSense',
        body: 'Tarkov Stash tercihlerinizi hatırlamak ve kullanıcı deneyimini iyileştirmek için çerezler kullanabilir. Google dahil üçüncü taraf sağlayıcılar, kullanıcının bu siteyi veya diğer siteleri önceki ziyaretlerine göre reklam sunmak için çerezler kullanır. Google reklam çerezleri, Google ve iş ortaklarının kullanıcılara bu site ve internetteki diğer sitelere yapılan ziyaretlere göre reklam göstermesini sağlar.',
      },
      {
        title: 'Üçüncü Taraf Hizmetler',
        body: 'Google AdSense, Firebase, analiz araçları ve tarkov.dev API entegrasyonları gibi hizmetleri kullanabiliriz. Bu hizmetler verileri kendi gizlilik politikalarına göre işleyebilir.',
      },
      {
        title: 'Tercihleriniz',
        body: 'Tarayıcı ayarlarınızdan çerezleri devre dışı bırakabilirsiniz. Ayrıca Google Ads Settings üzerinden kişiselleştirilmiş reklamlardan çıkabilirsiniz.',
      },
      {
        title: 'İletişim',
        body: 'Gizlilik sorularınız için contact@tarkovstash.com adresinden bize ulaşabilirsiniz.',
      },
    ],
  },
};

function PrivacyPolicy() {
  const { language } = useLanguage();
  const location = useLocation();
  const lang = (getLanguageFromPath(location.pathname) || language) === 'tr' ? 'tr' : 'en';
  const copy = content[lang];

  return (
    <main className="max-w-4xl mx-auto px-6 py-12 text-zinc-200">
      <h1 className="text-3xl md:text-4xl font-black text-white mb-3">{copy.title}</h1>
      <p className="text-xs text-zinc-500 mb-8">{copy.updated}</p>
      <p className="text-zinc-300 leading-relaxed mb-8">{copy.intro}</p>
      <div className="space-y-7">
        {copy.sections.map((section) => (
          <section key={section.title}>
            <h2 className="text-xl font-bold text-white mb-2">{section.title}</h2>
            <p className="text-sm leading-7 text-zinc-400">{section.body}</p>
          </section>
        ))}
      </div>
    </main>
  );
}

export default PrivacyPolicy;
